// utils/supply.js
//
// "Time until empty" — what the tank card strip and its popup say. Pure, so
// the wording and rounding are tested (supply.test.js).
//
// The cloud stores each tank's daily-use RATE (devices/{id}.supply.tankN,
// from functions/supply.js). This divides the live gallons by it, so the
// estimate follows the tank through the day.
//
// Rounding: always DOWN (ordering water early is fine, late is not); whole
// days under 2 weeks, weeks to 9 weeks, "About 2 months" to 13 weeks, then
// "Over 3 months". Each label is at most the real estimate.

export const SOON_DAYS = 7;      // copper below this (trial value; later the customer's own setting)
const WEEKS_UNTIL = 63;          // 9 weeks: under this, whole weeks (2–8)
const MAX_DAYS = 91;             // 13 weeks: under this, "About 2 months"

export function roundSupply(days) {
  if (days == null || !Number.isFinite(days) || days < 0) return null;
  if (days < 1) return 'Less than a day';
  if (days < 14) { const d = Math.floor(days); return `About ${d} day${d === 1 ? '' : 's'}`; }
  if (days < WEEKS_UNTIL) return `About ${Math.floor(days / 7)} weeks`;
  if (days < MAX_DAYS) return 'About 2 months';
  return 'Over 3 months';
}

/** The highest low-water alert level (%) still below the tank, or null. */
export function nextLowAlertPct(alerts, pct) {
  if (!alerts || typeof pct !== 'number') return null;
  const t = alerts.thresholds || {};
  const levels = [];
  if (t.low30) levels.push(30);
  if (t.low20) levels.push(20);
  if (t.low10) levels.push(10);
  if (t.custom && t.custom.on === true && typeof t.custom.value === 'number') levels.push(t.custom.value);
  const below = levels.filter((v) => v < pct);
  return below.length ? Math.max(...below) : null;
}

/**
 * What to show for one tank.
 *   est     devices/{id}.supply.tankN (or undefined)
 *   gallons the tank's usable gallons now (null if no capacity is set)
 *   pct     the tank's % now (same reading the gallons come from)
 *   alerts  the viewer's claim alerts (for the next low-water alert)
 *   offline the monitor isn't reporting
 * Returns null when there's nothing to show (no capacity set).
 */
export function supplyView({ est, gallons, pct, alerts, offline }) {
  if (offline) {
    return { state: 'waiting', big: 'Waiting for readings' };
  }
  if (!est || est.status !== 'ok') {
    return { state: 'gathering', big: 'Gathering data' };
  }
  if (gallons == null || !Number.isFinite(gallons)) return null;

  const rate = est.rate;
  const recent = est.basis === 'recent';
  const rateText = recent ? est.recent : est.typical;
  const days = rate > 0 ? Math.max(0, gallons) / rate : Infinity;
  const lowUse = !(rate > 0) || days >= MAX_DAYS;

  let nextAlert = null;
  const alertPct = nextLowAlertPct(alerts, pct);
  if (alertPct != null && pct > 0) {
    const galToAlert = gallons * (1 - alertPct / pct);
    const d = rate > 0 ? galToAlert / rate : Infinity;
    // Only worth a second figure when it's sooner than "Over 3 months".
    if (rate > 0 && d < MAX_DAYS) nextAlert = { pct: alertPct, label: roundSupply(d) };
  }

  return {
    state: 'estimate',
    big: lowUse ? 'Over 3 months' : roundSupply(days),
    // The popup's basis line (the card is one line: label and figure).
    sheetLine: !(rateText >= 1)
      ? 'Almost no use from this tank in the past two weeks.'
      : (recent
        ? `Based on your recent use of ${rateText} gal/day, assuming no rain.`
        : `Based on your typical use of ${rateText} gal/day, assuming no rain.`),
    soon: !lowUse && days < SOON_DAYS,
    days,
    nextAlert,
  };
}
